// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { removeTypename } from '../../../common/utils'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import type { SalesIncentiveUnit } from '../../station/stationTypes'
import ImageOverlay from '../ImageOverlay/ImageOverlay'
import RequiresAction from '../RequiresAction'
import WeeklyInfoPage from '../WeeklyInfoPage'
import Images from '../components/Images'
import Links from '../components/Links'
import { fetchRestaurantPageScoreCardStats, fetchWeeklyInfo, fetchWeeklyInfoScoreCard } from '../weeklyInfoQueries'
import type { WeeklyInfoCustomerPath, WeeklyInfoRestaurant, WeeklyInfoVariables } from '../weeklyInfoTypes'
import css from './restaurantPage.css'

const getSalesIncentiveUnitText = (salesIncentiveUnit: SalesIncentiveUnit): string =>
  salesIncentiveUnit === 'Euro' ? '€' : 'kpl'

type SalesIncentiveRowProps = {
  stationId: string
  date: string
  salesIncentiveUnit: SalesIncentiveUnit
  data: WeeklyInfoRestaurant
  canEdit: boolean
}

const SalesIncentiveRow = ({ stationId, date, salesIncentiveUnit, data, canEdit }: SalesIncentiveRowProps) => {
  const target = data.salesIncentiveTarget || 0
  const actual = data.totalActualSalesIncentive || 0
  const currentWeekTarget = data.currentWeekSalesIncentiveTarget || 0
  const currentWeekActual = data.currentWeekTotalActualSalesIncentive || 0
  const efficiency = actual && target ? Math.round((actual / target) * 100) : 0
  const currentWeekEfficiency =
    currentWeekActual && currentWeekTarget ? Math.round((currentWeekActual / currentWeekTarget) * 100) : 0
  return (
    <TableRow>
      <TableCell className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`weeklyInfo.heading.profitCenter${data.profitCenterType}`)}
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(target)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(actual)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: efficiency >= 100,
          [css.negativeCell]: efficiency < 100,
        })}
      >
        {efficiency} %
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(currentWeekTarget)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(currentWeekActual)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: currentWeekEfficiency >= 100,
          [css.negativeCell]: currentWeekEfficiency < 100,
        })}
      >
        {currentWeekEfficiency} %
      </TableCell>

      <TableCell className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            weeklyInfoId={data.id}
            subject='Restaurant'
            isActionRequired={data.isActionRequired}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

type SalesIncentiveProps = {
  stationId: string
  date: string
  salesIncentiveUnit: SalesIncentiveUnit
}

const SalesIncentive = ({ stationId, date, salesIncentiveUnit }: SalesIncentiveProps) => (
  <Query<Array<WeeklyInfoRestaurant>, WeeklyInfoVariables>
    query={fetchWeeklyInfo}
    variables={{
      stationId,
      date,
      type: 'Restaurant',
    }}
  >
    {({ data, error, loading }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.salesIncentive}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('weeklyInfo.title.salesIncentive', {
              week: `vko. ${moment(date).isoWeek()} ${moment(date).startOf('week').format('D.M.')}-${moment(date)
                .endOf('week')
                .format('D.M.')}`,
            })}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('weeklyInfo.heading.profitCenterName')}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.target', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.actual', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.successRate')}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.currentWeekTarget', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.currentWeekActual', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.currentWeekSuccessRate')}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {get(data, 'weeklyInfo', []).map((item) => (
                      <SalesIncentiveRow
                        stationId={stationId}
                        date={date}
                        salesIncentiveUnit={salesIncentiveUnit}
                        data={item}
                        canEdit={hasPermission}
                        key={item.id}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

type CustomerPathRowProps = {
  stationId: string
  date: string
  data: WeeklyInfoCustomerPath
  canEdit: boolean
}

const CustomerPathRow = ({ stationId, date, data, canEdit }: CustomerPathRowProps) => (
  <TableRow>
    <TableCell className={cx(css.tableCell)}>{data.description}</TableCell>
    <TableCell className={cx(css.tableCell, css.actionCell)}>
      {canEdit && (
        <RequiresAction
          stationId={stationId}
          date={date}
          weeklyInfoId={data.id}
          subject='Restaurant'
          isActionRequired={data.isActionRequired}
        />
      )}
    </TableCell>
  </TableRow>
)

type CustomerPathProps = {
  stationId: string
  date: string
}

const CustomerPath = ({ stationId, date }: CustomerPathProps) => (
  <Query<Array<WeeklyInfoCustomerPath>, WeeklyInfoVariables>
    query={fetchWeeklyInfo}
    variables={{
      stationId,
      date,
      type: 'CustomerPath',
    }}
  >
    {({ data, error, loading }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.customerPath}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('weeklyInfo.title.customerPath')}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={css.tableHeading}>{i18n.t('weeklyInfo.heading.taskName')}</TableCell>
                      <TableCell className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {get(data, 'weeklyInfo', []).map((item) => (
                      <CustomerPathRow
                        stationId={stationId}
                        date={date}
                        data={item}
                        canEdit={hasPermission}
                        key={item.id}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

const ScoreCardTable = ({ stationId, date, currentWeek }) => {
  const getColorClassName = ([category, values]: Row, type: string): string | null | undefined => {
    const { actual, high, low } = values[type]
    let className

    if (!actual && actual !== 0) {
      return null
    }

    className = css.colorCircleYellow

    if (actual >= high) {
      className = css.colorCircleGreen
    } else if (actual < low) {
      className = css.colorCircleRed
    }

    return className
  }

  const rowOrder = [
    'dailyChecklistUsage',
    'continuingChecklistUsageMonthly',
    'continuingChecklistUsageWeekly',
    'continuingChecklistUsageDaily',
    'toiletChecks',
    'hourlyHallChecklistUsage',
  ]
  const orderMax = rowOrder.length
  return (
    <Query
      query={fetchRestaurantPageScoreCardStats}
      variables={{
        stationId,
        date,
      }}
    >
      {({ loading, error, data }) => {
        if (loading || error) {
          return <CircularProgress />
        }

        const rows = Object.entries(removeTypename(data.scoreCardStats)).sort((a, b) => {
          const aValue = rowOrder.indexOf(a[0]) >= 0 ? rowOrder.indexOf(a[0]) : orderMax + 1
          const bValue = rowOrder.indexOf(b[0]) >= 0 ? rowOrder.indexOf(b[0]) : orderMax + 1
          if (aValue < bValue) return -1
          if (aValue > bValue) return 1
          return 0
        })
        return (
          <Paper className={css.scoreCard}>
            <Table className={css.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderName')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderRollingMonth')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderLastWeek')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderThisWeek')}</TableCell>
                  <TableCell className={cx(css.tableHeading, css.actionCell)}>
                    {i18n.t('weeklyInfo.heading.isActionRequired')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: any, i: number) => {
                  const [category, values] = row
                  const { thisWeek, lastWeek, rollingMonth } = values || {}

                  if (values) {
                    return (
                      <TableRow key={i}>
                        <TableCell className={css.tableCell}>{i18n.t(`scoreCard.${category}`)}</TableCell>
                        <TableCell className={css.tableCell}>
                          <div className={css.cellValue}>
                            <span className={cx(css.colorCircle, getColorClassName(row, 'rollingMonth'))} />
                            {rollingMonth.actual}
                          </div>
                        </TableCell>
                        <TableCell className={css.tableCell}>
                          <div className={css.cellValue}>
                            <span className={cx(css.colorCircle, getColorClassName(row, 'lastWeek'))} />
                            {lastWeek.actual}
                          </div>
                        </TableCell>
                        <TableCell className={css.tableCell}>
                          {values.thisWeek && (
                            <div className={css.cellValue}>
                              <span className={cx(css.colorCircle, getColorClassName(row, 'thisWeek'))} />
                              {thisWeek.actual}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className={css.actionCell}>
                          <Query
                            query={fetchWeeklyInfoScoreCard}
                            variables={{
                              stationId,
                              date,
                              type: 'ScoreCard',
                              row: category,
                            }}
                          >
                            {({ loading, error, data }) => {
                              if (loading || error) {
                                return <CircularProgress />
                              }

                              const info = get(data, 'weeklyInfo.0', {})
                              return (
                                <RequiresAction
                                  stationId={stationId}
                                  date={date}
                                  subject='Restaurant'
                                  isActionRequired={info.isActionRequired}
                                  weeklyInfoId={info.id}
                                />
                              )
                            }}
                          </Query>
                        </TableCell>
                      </TableRow>
                    )
                  } else {
                    return null
                  }
                })}
              </TableBody>
            </Table>
          </Paper>
        )
      }}
    </Query>
  )
}

@withUser
class RestaurantPage extends React.Component {
  constructor() {
    super()
    this.state = {
      imageOverlayIsOpen: false,
      selectedImage: null,
    }
  }

  selectImage = (imageContent: string) => {
    this.setState({
      selectedImage: imageContent,
      imageOverlayIsOpen: true,
    })
  }

  render() {
    const { user } = this.props
    const { imageOverlayIsOpen, selectedImage } = this.state
    return (
      <WeeklyInfoPage className={css.component} subject='Restaurant'>
        {({ stationId, salesIncentiveUnit, date, currentWeek, dateFormat, onDateChange, companyId }) => (
          <div className={css.content}>
            <ImageOverlay
              isOpen={imageOverlayIsOpen}
              image={selectedImage}
              onClose={() =>
                this.setState({
                  selectedImage: null,
                  imageOverlayIsOpen: false,
                })
              }
            />

            <SalesIncentive stationId={stationId} date={date} salesIncentiveUnit={salesIncentiveUnit} />

            <ScoreCardTable stationId={stationId} date={date} currentWeek={currentWeek} />

            <CustomerPath stationId={stationId} date={date} />

            <Images stationId={stationId} selectImage={this.selectImage} user={user} imageType={'weekinfo_hall'} />

            <Links stationId={stationId} linkType={'weekinfo_hall'} companyId={companyId} />
          </div>
        )}
      </WeeklyInfoPage>
    )
  }
}

export default RestaurantPage
